import React, { useState } from "react";

export default function Positions({
  positions,
  showActions = false,
  enableTrailStop = false,
  trailStatus = { active: false, status: "IDLE" },
  actionsDisabled = false,
  allowReconciliation = false
}) {
  const safePositions = Array.isArray(positions) ? positions : [];
  const [loading, setLoading] = useState(false);
  const [trailLoading, setTrailLoading] = useState(false);
  const [reconcileLoading, setReconcileLoading] = useState("");
  const [showClosedPositions, setShowClosedPositions] = useState(false);

  const localDateKey = (value) => {
    if (!value) return "";
    const isoDate = String(value).match(/^\d{4}-\d{2}-\d{2}/)?.[0];
    if (isoDate) return isoDate;
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return "";
    return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, "0")}-${String(parsed.getDate()).padStart(2, "0")}`;
  };

  const today = new Date();
  const previousDay = new Date(today);
  previousDay.setDate(today.getDate() - 1);
  const retainedClosedDates = new Set([localDateKey(today), localDateKey(previousDay)]);

  const openPositions = safePositions.filter(
    p => Number(p.qty || 0) !== 0 && !p.closed
  );
  const closedPositions = safePositions.filter(
    p => (Number(p.qty || 0) === 0 || p.closed)
      && retainedClosedDates.has(localDateKey(p.closed_at || p.trading_date))
  );

  const groupBySymbol = (items, closed = false) => {
    const groups = items.reduce((result, position) => {
      const symbol = position.tradingsymbol || "Unknown Symbol";
      if (!result[symbol]) result[symbol] = [];
      result[symbol].push(position);
      return result;
    }, {});

    return Object.entries(groups)
      .sort(([symbolA, rowsA], [symbolB, rowsB]) => {
        if (closed) {
          const latestA = Math.max(
            ...rowsA.map(p => Date.parse(p.closed_at || p.trading_date || 0) || 0)
          );
          const latestB = Math.max(
            ...rowsB.map(p => Date.parse(p.closed_at || p.trading_date || 0) || 0)
          );
          if (latestA !== latestB) return latestB - latestA;
        }
        return symbolA.localeCompare(symbolB);
      })
      .map(([symbol, rows]) => ({
        symbol,
        rows: rows.sort((a, b) => {
          if (a.client_id === "admin") return -1;
          if (b.client_id === "admin") return 1;
          return String(a.client_id || "").localeCompare(String(b.client_id || ""));
        })
      }));
  };

  const openSymbolGroups = groupBySymbol(openPositions);
  const closedSymbolGroups = groupBySymbol(closedPositions, true);

  const squareOffAll = async () => {
    try {
      setLoading(true);

      const res = await fetch("/api/square-off-all", {
        method: "POST"
      });

      const data = await res.json();

      if (!res.ok) {
        alert(data.error || "Square-off failed");
        return;
      }

      alert("All positions squared off ✅");
    } catch (err) {
      console.error(err);
      alert("Network error");
    } finally {
      setLoading(false);
    }
  };

  const getPnlColor = (pnl) => (pnl >= 0 ? "#16c784" : "#ea3943");

  const hasTradePositions = safePositions.some(p => p.source === "TRADE");
  const hasOpenBuyTradePositions = safePositions.some(
    p => p.source === "TRADE" && Number(p.qty || 0) > 0 && !p.closed
  );
  const isTrailActive = Boolean(trailStatus?.active);
  const isTrailTriggered = trailStatus?.status === "TRIGGERED";
  const isTrailReconciliationRequired = (
    trailStatus?.status === "RECONCILIATION_REQUIRED"
  );
  const trailSymbol = trailStatus?.symbol;
  const rawGroupPoints = trailStatus?.group_points;
  const groupPoints = Number(rawGroupPoints);
  const hasGroupPoints = rawGroupPoints != null && Number.isFinite(groupPoints);
  const isTrailEligible = Boolean(trailStatus?.trail_eligible);
  const trailActivationDisabled = (
    trailLoading
    || !hasOpenBuyTradePositions
    || !isTrailEligible
    || actionsDisabled
  );

  const startTrailStop = async () => {
    try {
      setTrailLoading(true);

      const res = await fetch("/api/copy-trade/trail-stop/start", {
        method: "POST"
      });

      const data = await res.json();

      if (!res.ok || data.success === false) {
        alert(data.message || data.error || "Trail Stop failed");
        return;
      }

      alert("Manual trail protection started");
    } catch (err) {
      console.error(err);
      alert("Network error");
    } finally {
      setTrailLoading(false);
    }
  };

  const stopTrailStop = async () => {
    const mode = trailStatus?.automatic ? "automatic" : "manual";
    const confirmed = window.confirm(
      `Stop ${mode} trail protection for ${trailSymbol || "this position"}? ` +
      "The position will remain open without this stop protection until you start it manually or close the position."
    );
    if (!confirmed) return;

    try {
      setTrailLoading(true);

      const res = await fetch("/api/copy-trade/trail-stop/stop", {
        method: "POST"
      });

      const data = await res.json();

      if (!res.ok || data.success === false) {
        alert(data.message || data.error || "Stop Trail failed");
        return;
      }
    } catch (err) {
      console.error(err);
      alert("Network error");
    } finally {
      setTrailLoading(false);
    }
  };

  const fmt = (value) => {
    const n = Number(value || 0);
    return n ? n.toFixed(2) : "0.00";
  };

  const money = (value) => Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  const lotsLabel = (p) => {
    if (p.lots == null || !Number(p.lot_size || 0)) return "—";
    return `${Number(p.lots).toLocaleString("en-IN")} × ${Number(p.lot_size).toLocaleString("en-IN")}`;
  };

  const productLabel = (productId) => String(productId || "Unknown product")
    .replace("AUTO_INDEX_", "Auto Index · ")
    .replace("INDEXOPTION_GAPSYNC_", "GapSync · ")
    .replace("INDEXOPTION_SYNC_", "Sync · ")
    .replaceAll("_", " ");

  const reconcilePosition = async (p) => {
    const key = `${p.client_id}-${p.tradingsymbol}`;
    try {
      setReconcileLoading(key);
      const response = await fetch(
        `/api/positions/${encodeURIComponent(p.client_id)}/${encodeURIComponent(p.tradingsymbol)}/reconcile`,
        { method: "POST" }
      );
      const result = await response.json();
      if (!response.ok || result.success === false) {
        alert(result.detail || result.message || "Broker reconciliation failed");
        return;
      }
      alert(result.message);
    } catch (error) {
      console.error(error);
      alert("Unable to verify the broker position");
    } finally {
      setReconcileLoading("");
    }
  };

  const renderCard = (p) => (
    <div
      key={`${p.client_id}-${p.tradingsymbol}`}
      style={{
        background: "#1a1a1a",
        border: "1px solid #2a2a2a",
        borderRadius: "8px",
        padding: "8px 10px",
        marginTop: "6px",
        transition: "0.2s"
      }}
    >
      {/* 🔹 Top Row */}
      <div style={{ display: "flex", justifyContent: "space-between", gap: "8px" }}>
        <strong style={{ fontSize: "13px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {p.client_id === "admin" ? "Admin" : p.client_id}
        </strong>

        {p.market_data_stale && !p.closed && (
          <span style={{ color: "#fca5a5", fontSize: "10px", fontWeight: "bold" }}>
            LTP STALE
          </span>
        )}

        <span
          style={{
            color: getPnlColor(p.trade_pnl ?? p.pnl),
            fontWeight: "bold",
            fontSize: "13px"
          }}
        >
          P&amp;L ₹{money(p.trade_pnl ?? p.pnl)}
        </span>
      </div>

      {/* 🔹 Middle Row */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontSize: "11px",
          marginTop: "4px",
          opacity: 0.8
        }}
      >
        <span>Lots: {lotsLabel(p)}</span>
        <span>Trade Qty: {Number(p.trade_qty || Math.abs(Number(p.qty || 0))).toLocaleString("en-IN")}</span>
        <span>{p.source || "TRADE"}</span>
      </div>

      {/* 🔹 Price Row */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontSize: "11px",
          marginTop: "4px"
        }}
      >
        <span style={{ color: "#aaa" }}>
          Entry: ₹{p.entry_price?.toFixed(2) ?? "-"}
        </span>
        <span style={{ color: "#aaa" }}>
          LTP: ₹{p.last_price?.toFixed(2) ?? "-"}
        </span>
        <span style={{ color: "#aaa" }}>
          Exit: ₹{p.exit_price?.toFixed(2) ?? "-"}
        </span>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          gap: "6px",
          fontSize: "11px",
          marginTop: "6px",
          paddingTop: "6px",
          borderTop: "1px solid #2a2a2a"
        }}
      >
        <span style={{ color: "#aaa" }}>
          {p.source === "AUTO_STOCK" ? "Capital Used" : "Premium Used"}: ₹{money(
            p.source === "AUTO_STOCK" ? p.capital_used : p.premium_used
          )}
        </span>
        <span style={{ color: "#aaa", textAlign: "center" }}>
          {p.closed ? "Exit Value" : "Market Value"}: ₹{money(
            p.closed ? p.exit_value : p.market_value
          )}
        </span>
        <span style={{ color: getPnlColor(Number((p.source === "AUTO_STOCK" ? p.capital_return_pct : p.return_pct) || 0)), textAlign: "right" }}>
          Return: {(p.source === "AUTO_STOCK" ? p.capital_return_pct : p.return_pct) == null ? "—" : `${Number(p.source === "AUTO_STOCK" ? p.capital_return_pct : p.return_pct).toFixed(2)}%`}
        </span>
      </div>

      {p.source === "ALGO" && (
        <div
          style={{
            marginTop: "7px",
            padding: "7px",
            border: `1px solid ${p.ownership_status === "SHORTFALL" ? "#b91c1c" : "#334155"}`,
            borderRadius: "6px",
            background: p.ownership_status === "SHORTFALL" ? "#2b1111" : "#101827",
            fontSize: "11px"
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", gap: "8px", fontWeight: "bold" }}>
            <span style={{ color: "#bfdbfe" }}>Product Ownership</span>
            <span style={{ color: p.ownership_status === "SHORTFALL" ? "#fca5a5" : p.ownership_status === "VERIFIED" ? "#86efac" : "#fbbf24" }}>
              {p.ownership_status || "UNVERIFIED"}
            </span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "3px 10px", marginTop: "5px", color: "#cbd5e1" }}>
            <span>MintX Total: {Number(p.mintx_owned_qty ?? p.qty ?? 0).toLocaleString("en-IN")}</span>
            <span>Broker Total: {p.last_broker_verified_qty == null ? "Awaiting sync" : Number(p.last_broker_verified_qty).toLocaleString("en-IN")}</span>
            {(p.product_ownership || []).map(owner => (
              <React.Fragment key={owner.product_id}>
                <span>{productLabel(owner.product_id)}</span>
                <span style={{ textAlign: "right" }}>
                  Qty {Number(owner.qty || 0).toLocaleString("en-IN")} · Avg ₹{money(owner.avg_price)}
                </span>
              </React.Fragment>
            ))}
          </div>
          <div style={{ color: "#64748b", marginTop: "4px" }}>
            Broker checked: {p.last_broker_verified_at ? new Date(p.last_broker_verified_at).toLocaleString("en-IN") : "Not checked yet"}
          </div>
        </div>
      )}

      {/* 🔹 Bottom Row */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontSize: "11px",
          marginTop: "4px"
        }}
      >
        <span style={{ color: "#aaa" }}>
          Realized: ₹{money(p.realized_pnl)}
        </span>
        <span style={{ color: "#aaa" }}>
          Unrealized: ₹{money(p.unrealized_pnl)}
        </span>
        <span style={{ color: "#aaa" }}>
          Status: {p.closed ? "Closed" : "Open"}
        </span>
      </div>

      {!p.closed && p.client_id !== "admin" && (p.reconciliation_required || allowReconciliation) && (
        <div
          style={{
            marginTop: "7px",
            padding: "7px",
            border: "1px solid #b45309",
            borderRadius: "6px",
            background: "#291707",
            fontSize: "11px"
          }}
        >
          <div style={{ color: "#fbbf24", fontWeight: "bold" }}>
            {p.reconciliation_required
              ? "Broker reconciliation required"
              : "Position still shown as open?"}
          </div>
          <div style={{ color: "#d6d3d1", marginTop: "2px" }}>
            {p.reconciliation_required
              ? p.reconciliation_reason || "Position status could not be confirmed"
              : "Check the broker and update MintX from the broker's current position quantity."}
          </div>
          {allowReconciliation && (
            <button
              type="button"
              onClick={() => reconcilePosition(p)}
              disabled={reconcileLoading === `${p.client_id}-${p.tradingsymbol}`}
              style={{
                marginTop: "6px",
                padding: "5px 8px",
                border: "none",
                borderRadius: "5px",
                background: "#d97706",
                color: "white",
                cursor: "pointer",
                fontSize: "11px",
                fontWeight: "bold"
              }}
            >
              {reconcileLoading === `${p.client_id}-${p.tradingsymbol}`
                ? "Checking broker..."
                : "Check Broker & Reconcile"}
            </button>
          )}
        </div>
      )}
    </div>
  );

  const renderSymbolGroups = (groups) => groups.map(group => {
    const isAutoStockGroup = group.rows.every(p => p.source === "AUTO_STOCK");
    const clients = new Set(group.rows.map(p => p.client_id)).size;
    const totalLots = group.rows.reduce((sum, p) => sum + Number(p.lots || 0), 0);
    const totalQty = group.rows.reduce(
      (sum, p) => sum + Number(
        isAutoStockGroup
          ? Math.abs(Number(p.qty || 0))
          : (p.trade_qty || Math.abs(Number(p.qty || 0)))
      ),
      0
    );
    const totalCapital = group.rows.reduce(
      (sum, p) => sum + Number(
        isAutoStockGroup ? p.capital_used : p.premium_used
      ),
      0
    );
    const totalPnl = group.rows.reduce(
      (sum, p) => sum + Number(p.trade_pnl ?? p.pnl ?? 0),
      0
    );

    return (
    <section key={group.symbol} style={{ marginTop: "10px" }}>
      <div
        style={{
          padding: "6px 8px",
          borderRadius: "6px",
          background: "#172033",
          border: "1px solid #334155",
          color: "#bfdbfe",
          fontSize: "12px",
          fontWeight: "bold",
          display: "grid",
          gap: "3px"
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", gap: "8px" }}>
          <span>{group.symbol}</span>
          <span style={{ color: getPnlColor(totalPnl) }}>P&amp;L ₹{money(totalPnl)}</span>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: "8px",
            flexWrap: "wrap",
            color: "#94a3b8",
            fontSize: "10px",
            fontWeight: "normal"
          }}
        >
          <span>{clients} client{clients === 1 ? "" : "s"}</span>
          <span>Lots: {totalLots ? totalLots.toLocaleString("en-IN") : "—"}</span>
          <span>Qty: {totalQty.toLocaleString("en-IN")}</span>
          <span>{isAutoStockGroup ? "Capital Used" : "Premium Used"}: ₹{money(totalCapital)}</span>
        </div>
      </div>
      {group.rows.map(p => renderCard(p))}
    </section>
    );
  });

  return (
    <div
      style={{
        height: "100%",
        minHeight: 0,
        overflowY: "auto",
        border: "1px solid #2a2a2a",
        padding: "10px",
        borderRadius: "8px",
        background: "#111",
        color: "#eaeaea"
      }}
    >
      {/* 🔥 Header */}
      <div
        style={{
          position: "sticky",
          top: 0,
          background: "#111",
          zIndex: 2,
          paddingBottom: "8px",
          borderBottom: "1px solid #2a2a2a"
        }}
      >

        {showActions && hasTradePositions && (
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "6px" }}>
            <button
              onClick={squareOffAll}
              disabled={loading || actionsDisabled}
              style={{
                backgroundColor: "#ea3943",
                color: "white",
                padding: "5px 10px",
                border: "none",
                borderRadius: "6px",
                cursor: loading ? "not-allowed" : "pointer",
                fontSize: "11px"
              }}
            >
              {loading ? "Closing..." : "Square Off All"}
            </button>

            {enableTrailStop && !isTrailActive && (
              <button
                onClick={startTrailStop}
                disabled={trailActivationDisabled}
                title={
                  !hasOpenBuyTradePositions
                    ? "No open BUY Copy Trade position"
                    : trailStatus?.eligibility_reason
                      || "Trail can be activated after group profit reaches 5 points"
                }
                style={{
                  backgroundColor: isTrailEligible ? "#f59e0b" : "#374151",
                  color: "white",
                  padding: "5px 10px",
                  border: "none",
                  borderRadius: "6px",
                  cursor: trailActivationDisabled ? "not-allowed" : "pointer",
                  fontSize: "11px"
                }}
              >
                {trailLoading ? "Starting..." : "Start Trail Manually"}
              </button>
            )}

            {enableTrailStop && isTrailActive && (
              <button
                onClick={stopTrailStop}
                disabled={trailLoading || isTrailTriggered || actionsDisabled}
                style={{
                  backgroundColor: "#64748b",
                  color: "white",
                  padding: "5px 10px",
                  border: "none",
                  borderRadius: "6px",
                  cursor: trailLoading || isTrailTriggered ? "not-allowed" : "pointer",
                  fontSize: "11px"
                }}
              >
                {isTrailTriggered ? "Sell Triggered" : trailLoading ? "Stopping..." : "Stop Trail"}
              </button>
            )}
          </div>
        )}

        {enableTrailStop && (hasOpenBuyTradePositions || isTrailActive) && (
          <div
            style={{
              marginTop: "8px",
              padding: "8px",
              border: "1px solid #92400e",
              borderRadius: "8px",
              background: "#1f1305",
              fontSize: "11px",
              lineHeight: 1.6
            }}
          >
            <div style={{ color: "#fbbf24", fontWeight: "bold" }}>
              {!isTrailActive
                ? "Copy Trade Group Points"
                : isTrailReconciliationRequired
                ? "Square-off Incomplete - Reconciliation Required"
                : trailStatus.market_data_stale
                ? "Manual Trail Paused - Stale Market Data"
                : isTrailTriggered
                  ? "Manual Trail Triggered"
                  : "Manual Trail Active"} {trailSymbol ? `- ${trailSymbol}` : ""}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 10px" }}>
              <span style={{ color: hasGroupPoints ? getPnlColor(groupPoints) : "#94a3b8", fontWeight: "bold" }}>
                P/L Points: {hasGroupPoints ? `${groupPoints >= 0 ? "+" : ""}${fmt(groupPoints)}` : "Unavailable"}
              </span>
              <span>LTP: ₹{fmt(trailStatus.group_ltp)}</span>
              <span>Max Client Buy: ₹{fmt(trailStatus.group_reference_price || trailStatus.entry_ltp)}</span>
              <span>
                Client Prices: {trailStatus.group_priced_client_count ?? trailStatus.client_ids?.length ?? 0}/
                {trailStatus.group_client_count ?? trailStatus.client_ids?.length ?? 0}
              </span>
              {isTrailActive && <span>High LTP: ₹{fmt(trailStatus.highest_ltp)}</span>}
              {isTrailActive && <span>Trail Stop: ₹{fmt(trailStatus.trail_stop)}</span>}
              <span style={{ color: isTrailEligible || isTrailActive ? "#86efac" : "#fca5a5", gridColumn: "1 / -1" }}>
                {isTrailActive
                  ? "Trail active — reference price is frozen"
                  : trailStatus.eligibility_reason || "Waiting for Copy Trade prices"}
              </span>
              {isTrailReconciliationRequired && (
                <span style={{ color: "#fca5a5", gridColumn: "1 / -1" }}>
                  Pending clients: {trailStatus.pending_client_ids?.join(", ") || "unknown"}. Verify and close these broker positions, then reconcile them in MintX.
                </span>
              )}
              {trailStatus.market_data_stale && (
                <span style={{ color: "#fca5a5" }}>
                  LTP age: {trailStatus.ltp_age_seconds == null
                    ? "unknown"
                    : `${Number(trailStatus.ltp_age_seconds).toFixed(1)}s`}
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* 🔥 Body */}
      {safePositions.length === 0 ? (
        <p style={{ opacity: 0.6 }}>No positions</p>
      ) : (
        <>
          {/* 🔹 ADMIN */}
          {openSymbolGroups.length > 0 ? (
            renderSymbolGroups(openSymbolGroups)
          ) : (
            <p style={{ opacity: 0.6 }}>No open positions</p>
          )}

          {/* 🔹 CLIENTS */}
          {closedSymbolGroups.length > 0 && (
            <section style={{ marginTop: "14px", borderTop: "1px solid #2a2a2a", paddingTop: "10px" }}>
              <button
                type="button"
                onClick={() => setShowClosedPositions(value => !value)}
                aria-expanded={showClosedPositions}
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "space-between",
                  background: "#18181b",
                  color: "#a1a1aa",
                  border: "1px solid #3f3f46",
                  borderRadius: "6px",
                  padding: "7px 9px",
                  cursor: "pointer",
                  fontSize: "12px",
                  fontWeight: "bold"
                }}
              >
                <span>Closed Positions / History</span>
                <span>{closedPositions.length} {showClosedPositions ? "▲" : "▼"}</span>
              </button>
              {showClosedPositions && renderSymbolGroups(closedSymbolGroups)}
            </section>
          )}
        </>
      )}
    </div>
  );
}
